import { buildAngularJsBridgeApi, registerAngularJsBridge } from './angularjs-bridge';
import { registerShortcut } from './keyboard';
import './panel/acme-dev-tools';
import { pluginRegistry } from './plugin-registry';
import { authSessionPlugin } from './plugins/auth-session/auth-session';
import { environmentInfoPlugin } from './plugins/environment-info/environment-info';
import { FeatureFlagBridge } from './plugins/feature-flags/feature-flag-bridge';
import { featureFlagsPlugin } from './plugins/feature-flags/feature-flags';
import { mswPlugin } from './plugins/msw-mocks/msw-mocks';
import {
  addMock,
  clearMocks,
  getMocks,
  getWorker,
  initMswWorker,
  rebuildRuntimeHandlers,
  removeMock,
  toggleMock,
  updateMock,
} from './plugins/msw-mocks/msw-worker';
import { pendoPlugin } from './plugins/pendo/pendo';
import { createBrowserInspector, xstatePlugin } from './plugins/xstate-inspector/xstate-inspector';
import { loadState, updateState } from './storage';

export function registerDefaultPlugins(): void {
  pluginRegistry.register(featureFlagsPlugin);
  pluginRegistry.register(environmentInfoPlugin);
  pluginRegistry.register(authSessionPlugin);
  pluginRegistry.register(pendoPlugin); // TODO: conditionally add Pendo plugin based on future PENDO_PUBLIC_APP_ID env variable
  pluginRegistry.register(xstatePlugin);
  if (window.CONFIG?.environment === 'development') {
    pluginRegistry.register(mswPlugin);
  }
}

export function buildWindowApi(
  featureFlagBridge: FeatureFlagBridge,
): NonNullable<NonNullable<Window['__ACME_B2B__']>['devTools']> {
  const inspectors = new Map<string, ReturnType<typeof createBrowserInspector>>();

  return {
    open: () => {
      let panel = document.querySelector('acme-dev-tools') as any;
      if (!panel) {
        panel = document.createElement('acme-dev-tools');
        document.body.appendChild(panel);
      }
      panel.open = true;
    },
    registerPlugin: (plugin) => pluginRegistry.register(plugin),
    registerInspector: (key: string) => {
      if (inspectors.has(key)) {
        return inspectors.get(key);
      }
      const inspector = createBrowserInspector();
      inspectors.set(key, inspector);
      pluginRegistry.notifyBadgeChange('xstate');
      return inspector;
    },
    listInspectors: () => Array.from(inspectors.keys()),
    getInspector: (key: string) => inspectors.get(key),
    getFeatureFlag: (key: string) => featureFlagBridge.getFlag(key) ?? false,
    getOriginalFeatureFlag: (key: string) => featureFlagBridge.getOriginalFlag(key) ?? false,
    setFeatureFlagOverride: (key: string, value: boolean | null) => {
      featureFlagBridge.setOverride(key, value);
      updateState((s) => {
        s.featureFlags.overrides = featureFlagBridge.getOverrides();
      });
    },
    getFeatureFlagOverrides: () => featureFlagBridge.getOverrides(),
    msw: { getWorker, addMock, removeMock, toggleMock, updateMock, clearMocks, getMocks },
  };
}

export function registerPanelShortcut(): () => void {
  return registerShortcut({
    key: 'o',
    mod: true,
    shift: true,
    handler: () => {
      let panel = document.querySelector('acme-dev-tools') as any;
      if (!panel) {
        panel = document.createElement('acme-dev-tools');
        document.body.appendChild(panel);
      }
      panel.open = !panel.open;
    },
  });
}

export function initDevTools(): void {
  const { featureFlags } = loadState();
  const featureFlagBridge = new FeatureFlagBridge(featureFlags.overrides, null);

  registerDefaultPlugins();

  if (window.CONFIG?.environment === 'development') {
    initMswWorker();
  }

  registerPanelShortcut();

  window.__ACME_B2B__ = window.__ACME_B2B__ ?? {};
  window.__ACME_B2B__.devTools = buildWindowApi(featureFlagBridge);

  window.addEventListener('acme:devtools-state-imported', () => {
    const newState = loadState();

    for (const key of Object.keys(featureFlagBridge.getOverrides())) {
      featureFlagBridge.setOverride(key, null);
    }

    for (const [key, value] of Object.entries(newState.featureFlags.overrides)) {
      featureFlagBridge.setOverride(key, value);
    }

    rebuildRuntimeHandlers();
  });

  registerAngularJsBridge((injector) => {
    const auth = buildAngularJsBridgeApi(injector, featureFlagBridge);
    window.__ACME_B2B__!.devTools = {
      ...window.__ACME_B2B__!.devTools!,
      getUserFromCookie: auth.getUserFromCookie,
      getCSRUserFromCookie: auth.getCSRUserFromCookie,
    };
  });
}
